import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useBookingActions } from './useBookingActions'

import { useApp } from '../../contexts/AppContext'
import { useConfirm } from '../../contexts/ConfirmContext'
import { Booking, BookingFunding, BookingStatus, CancellationResult } from '../../types/booking/booking'
import { ConfirmLayout } from '../../types/confirm/confirm'
import { BannerKind } from '../../types/contexts/AppContext'
import { CancelReason } from '../../types/court/court'
import { formatMinutes, formatPoints, timeOf } from '../../utils/booking/booking'
import { toDisplayOnlyDate } from '../../utils/dateUtils'

export const isUpcomingConfirmed = (booking: Booking) =>
  booking.status === BookingStatus.CONFIRMED && new Date(booking.startTime) > new Date()

/**
 * Customers can change a booking only until the club's notice period before the session (the server enforces it with
 * its own clock).
 */
export const inFreeWindow = (booking: Booking) =>
  !booking.freeCancellationUntil || new Date() <= new Date(booking.freeCancellationUntil)

export const canReschedule = (booking: Booking) =>
  isUpcomingConfirmed(booking) && inFreeWindow(booking) && booking.rescheduleCount < booking.maxReschedules

/**
 * A booking moved as many times as the club allows is final: the customer can no longer reschedule nor cancel it. A
 * club allowing no reschedules does not lock its bookings.
 */
export const rescheduleLimitReached = (booking: Booking) =>
  booking.rescheduleCount > 0 && booking.rescheduleCount >= booking.maxReschedules

/**
 * Bookings paid with prepaid minutes cannot be cancelled by the customer, only rescheduled; bookings that reached the
 * reschedule limit cannot be cancelled either.
 */
export const canCancel = (booking: Booking) =>
  isUpcomingConfirmed(booking) &&
  inFreeWindow(booking) &&
  !(booking.prepaidMinutes && booking.prepaidMinutes > 0) &&
  !rescheduleLimitReached(booking)

/**
 * Cancellation with an explanation of what the customer gets back: what was paid comes back as refund points, and (when
 * the club cancels) prepaid minutes return to their passes.
 */
export const useBookingCancellation = (returnTo: string, management = false) => {
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { cancelOwn, cancelByManagement } = useBookingActions()
  const { t, i18n } = useTranslation(['history', 'bookings'])

  const resultMessage = useCallback(
    (result: CancellationResult | null) => {
      if (!result) {
        return t('bookings:successfully-canceled')
      }
      const parts = [t('bookings:successfully-canceled')]
      if (result.restoredMinutes > 0) {
        parts.push(t('history.cancel-result.restored', { minutes: formatMinutes(result.restoredMinutes) }))
      }
      if (result.refundPoints > 0) {
        parts.push(t('history.cancel-result.points', { points: formatPoints(result.refundPoints) }))
      }
      if (result.forfeitedMinutes > 0) {
        parts.push(t('history.cancel-result.forfeited', { minutes: formatMinutes(result.forfeitedMinutes) }))
      }
      return parts.join(' ')
    },
    [t]
  )

  const confirmCancel = useCallback(
    (booking: Booking, reason: CancelReason = CancelReason.USER_REQUEST) => {
      const freeWindow = management || inFreeWindow(booking)
      const refundText = !freeWindow
        ? t('history.cancel-confirmation.outside-window')
        : booking.funding === BookingFunding.PASS
          ? t('history.cancel-confirmation.minutes-returned')
          : booking.funding === BookingFunding.FREE
            ? undefined
            : t('history.cancel-confirmation.points-issued')

      confirm({
        layout: ConfirmLayout.DETAILS,
        title: t('history.cancel-confirmation.title'),
        subtitle: t('history.cancel-confirmation.subtitle'),
        confirmLabel: t('history.cancel-confirmation.confirm'),
        backLabel: t('bookings:booking-confirmation.back-label'),
        returnTo,
        itemCards: [
          {
            id: booking.id,
            title: booking.courtName,
            details: [
              {
                label: t('bookings:booking-confirmation.date-label'),
                value: toDisplayOnlyDate(booking.startTime, i18n.language),
              },
              {
                label: t('bookings:booking-confirmation.start-time-label'),
                value: `${timeOf(booking.startTime)}–${timeOf(booking.endTime)}`,
              },
              ...(booking.ownerName
                ? [{ label: t('history.booking-history-list.tabel-header.customer'), value: booking.ownerName }]
                : []),
              ...(booking.freeCancellationUntil && !management
                ? [
                    {
                      label: t('history.cancel-confirmation.free-until'),
                      value: `${toDisplayOnlyDate(booking.freeCancellationUntil, i18n.language)} ${timeOf(
                        booking.freeCancellationUntil
                      )}`,
                    },
                  ]
                : []),
            ],
          },
        ],
        warning: refundText,
        onConfirm: async () => {
          const result = management
            ? await cancelByManagement(booking.id, reason)
            : await cancelOwn(booking.id)
          showBanner({ kind: BannerKind.OK, msg: resultMessage(result) })
        },
      })
    },
    [cancelByManagement, cancelOwn, confirm, i18n.language, management, resultMessage, returnTo, showBanner, t]
  )

  return { confirmCancel }
}
